import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Privacy Policy');

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <div
        className="relative py-20"
        style={{
          backgroundImage: `url('/hero-background.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto px-8">
          <button
            onClick={() => navigate('/')}
            className="mb-6 text-white hover:text-orange-400 flex items-center transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Terug naar Home
          </button>
          <h1 className="text-5xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Laatste update: Januari 2026
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-8 py-16">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8">
          {/* Introduction */}
          <section className="mb-8">
            <p className="text-gray-700 leading-relaxed">
              SnapLogic hecht veel waarde aan de bescherming van uw persoonsgegevens. In deze privacy policy
              leggen we uit welke gegevens we verzamelen, hoe we deze gebruiken en welke rechten u heeft.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">1. Welke gegevens verzamelen we?</h2>
            <p className="text-gray-700 mb-3">
              Wij verzamelen de volgende persoonsgegevens wanneer u gebruik maakt van onze diensten of contact met ons opneemt:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Naam en contactgegevens (e-mailadres, telefoonnummer)</li>
              <li>Bedrijfsnaam en projectinformatie</li>
              <li>Cookies en vergelijkbare technologieën</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">2. Waarvoor gebruiken we uw gegevens?</h2>
            <p className="text-gray-700 mb-3">
              Wij gebruiken uw persoonsgegevens voor de volgende doeleinden:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Het verwerken van uw aanvraag en het uitbrengen van offertes</li>
              <li>Communicatie over uw project en dienstverlening</li>
              <li>Het verbeteren van onze website en diensten</li>
              <li>Het naleven van wettelijke verplichtingen</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">3. Cookies</h2>
            <p className="text-gray-700 mb-3">
              Onze website maakt gebruik van cookies om uw ervaring te verbeteren. We gebruiken:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Functionele cookies:</strong> Noodzakelijk voor het functioneren van de website</li>
              <li><strong>Analytische cookies:</strong> Om het gebruik van onze website te analyseren</li>
              <li><strong>Marketing cookies:</strong> Voor gepersonaliseerde advertenties (alleen met toestemming)</li>
            </ul>
            <p className="text-gray-700 mt-3">
              U kunt uw cookievoorkeuren op elk moment wijzigen via de cookie-instellingen van uw browser.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">4. Beveiliging</h2>
            <p className="text-gray-700">
              Wij nemen passende technische en organisatorische maatregelen om uw persoonsgegevens te beschermen
              tegen ongeautoriseerde toegang, verlies of wijziging. Dit omvat onder andere het gebruik van
              SSL-certificaten, beveiligde servers en toegangscontroles.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">5. Bewaartermijn</h2>
            <p className="text-gray-700">
              Wij bewaren uw persoonsgegevens niet langer dan noodzakelijk voor de doeleinden waarvoor ze zijn
              verzameld, tenzij een langere bewaartermijn wettelijk is vereist.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">6. Uw rechten</h2>
            <p className="text-gray-700 mb-3">
              U heeft de volgende rechten met betrekking tot uw persoonsgegevens:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Recht op inzage in uw gegevens</li>
              <li>Recht op correctie van onjuiste gegevens</li>
              <li>Recht op verwijdering van uw gegevens</li>
              <li>Recht op beperking van de verwerking</li>
              <li>Recht op dataportabiliteit</li>
              <li>Recht van bezwaar tegen verwerking</li>
            </ul>
            <p className="text-gray-700 mt-3">
              Om deze rechten uit te oefenen, kunt u contact met ons opnemen via de onderstaande contactgegevens.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">7. Delen met derden</h2>
            <p className="text-gray-700">
              Wij verkopen uw gegevens niet aan derden. We kunnen uw gegevens wel delen met vertrouwde partners
              die ons helpen bij het leveren van onze diensten, zoals hostingproviders en betalingsdiensten.
              Deze partijen zijn contractueel verplicht om zorgvuldig met uw gegevens om te gaan.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">8. Wijzigingen in deze privacy policy</h2>
            <p className="text-gray-700">
              Wij kunnen deze privacy policy van tijd tot tijd wijzigen. De meest recente versie staat altijd
              op deze pagina. We raden u aan deze pagina regelmatig te raadplegen.
            </p>
          </section>

          {/* Contact Section */}
          <section className="bg-blue-50 p-6 rounded-xl">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Contact</h2>
            <p className="text-gray-700 mb-4">
              Heeft u vragen over deze privacy policy of wilt u uw rechten uitoefenen? Neem dan contact met ons op:
            </p>
            <div className="text-gray-700">
              <p className="mb-2"><strong>SnapLogic</strong></p>
              <p className="mb-2">📧 E-mail: info@snaplogic.nl</p>
              <p className="mb-2">📱 Telefoon: 06-10654827</p>
              <p className="mb-2">📍 Adres: Cornelis van der Lijnstraat 230, 2593 NP 's-Gravenhage</p>
              <p>🏢 KvK: 97231762</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
